/**
 * Open a streaming subscription. Dispatches a `patchwork:subscribe` for the
 * given `selector` carrying a fresh `MessageChannel` port; the answering
 * provider pushes values back over that channel for as long as the
 * subscription is live. `listener` is invoked once per emission (the first
 * delivery is always async because `MessagePort.postMessage` queues a task).
 *
 * The event is dispatched from the nearest enclosing `<patchwork-view>` so
 * callers can pass any node inside a provider subtree. An unclaimed
 * subscription is never settled: if no provider answers, `listener` simply
 * never fires.
 *
 * Returns an unsubscribe function. Calling it tells the provider to tear down
 * (via an `unsubscribe` message) and closes the consumer's port; any values
 * the provider emits after that are dropped.
 */
export function subscribe(element, selector, listener) {
    const view = element.closest("patchwork-view");
    const dispatchEl = view ?? element;
    const channel = new MessageChannel();
    const port = channel.port2;
    const controller = new AbortController();
    const { signal } = controller;
    port.addEventListener("message", (event) => {
        if (event.data?.type === "change")
            listener(event.data.value);
    }, { signal });
    // addEventListener (unlike assigning onmessage) does not implicitly start
    // the port, so message delivery has to be kicked off explicitly.
    port.start();
    const detail = {
        selector,
        port: channel.port1,
    };
    dispatchEl.dispatchEvent(new CustomEvent("patchwork:subscribe", {
        detail,
        bubbles: true,
        composed: true,
    }));
    return () => {
        if (signal.aborted)
            return;
        controller.abort();
        port.postMessage({ type: "unsubscribe" });
        port.close();
    };
}
/**
 * Answer a `patchwork:subscribe`. The `producer` receives a `respond`
 * callback it can call any number of times to push values to the consumer,
 * and may return a teardown that runs when the consumer unsubscribes. Stops
 * propagation so ancestor providers don't double-answer. Values emitted after
 * the consumer unsubscribes are dropped.
 *
 * `respond` takes an optional list of `Transferable`s as a second argument;
 * they are handed to `MessagePort.postMessage` so the named objects are moved
 * to the consumer instead of copied. Use this when the value carries something
 * that can't be (or is expensive to) structured-clone, e.g. a `MessagePort`
 * or a large `ArrayBuffer`. `T` is left unconstrained for this reason — once
 * you transfer, the value is no longer plain JSON.
 */
export function accept(event, producer) {
    event.stopPropagation();
    const port = event.detail.port;
    let alive = true;
    const respond = (value, transfer) => {
        if (!alive)
            return;
        port.postMessage({ type: "change", value }, transfer ?? []);
    };
    let teardown;
    try {
        teardown = producer(respond);
    }
    catch (err) {
        console.error("[patchwork-providers] subscribe producer threw:", err);
    }
    const stop = () => {
        if (!alive)
            return;
        alive = false;
        try {
            teardown?.();
        }
        catch (err) {
            console.error("[patchwork-providers] subscribe teardown threw:", err);
        }
        port.close();
    };
    port.onmessage = (event) => {
        if (event.data?.type === "unsubscribe")
            stop();
    };
}
/**
 * One-shot convenience wrapper over {@link subscribe}: opens a subscription
 * for `selector`, resolves with the first value a provider emits, then
 * immediately unsubscribes. Because there is no fallback provider, an
 * unclaimed selector never resolves — use `subscribe` directly if you need to
 * handle the "no provider" case.
 */
export function request(element, selector) {
    return new Promise((resolve) => {
        const unsubscribe = subscribe(element, selector, (value) => {
            // The first emission is always async (postMessage queues a task), so
            // `unsubscribe` is assigned by the time this fires.
            unsubscribe();
            resolve(value);
        });
    });
}
export { registerRepoProviderElement, } from "./repo-provider.js";
export { OverlayHandle } from "./overlay-handle.js";
export { OverlayRepo } from "./overlay-repo.js";
